// covered-by: frontends/carrie/src/routes/auth-layout/__tests__/authLayoutRoute.test.tsx
import { type ShowcaseToken } from "@mg/schemas/src/prince/auth";
import { QueryClient } from "@tanstack/react-query";
import {
  Outlet,
  // eslint-disable-next-line import/named
  createRoute,
  redirect,
  useRouter,
  matchPathname,
} from "@tanstack/react-router";

import { shouldRedirectToAddPassword } from "./onboardingGuard";
import { requireFlagsLoaded } from "./requireFlagsLoaded";

import { useAuthenticationOverlay } from "../../components/AuthenticationDialog";
import PunttLayout from "../../components/layouts/puntt";
import PendingComponent from "../../components/PendingComponent";
import { SIDEBAR_STORAGE_NAME } from "../../components/sidebar/SidebarProvider";
import { BASEPATH } from "../../config/env";
import { getGuidelines } from "../../services/brand-guidelines";
import { getEnterprises } from "../../services/enterprises";
import { getFolderTree } from "../../services/folders";
import { useAppSelector } from "../../utils/hooks";
import { logoutUrl } from "../../utils/logoutUrl";
import { queryClient } from "../../utils/queryClient";
import { logout } from "../../utils/slices/auth";
import { setDrawerOpen } from "../../utils/slices/ui";
import { store } from "../../utils/store";
import { addPasswordRoute } from "../add-password/route";
import { loginRoute } from "../login/route";
import { rootRoute } from "../root/route";
import { ticketsRoute } from "../tickets/route";
import { ticketRoute } from "../tickets/routes/ticket/route";
import { ticketCompatibilityRoute } from "../tickets/routes/ticket_compat";

// why: reading storage throws SecurityError rather than returning null when it is
// blocked (private mode, third-party-cookie blocking, sandboxed iframe). beforeLoad
// runs on every route resolution and logoutUrl from render, so an unguarded read
// white-screens the route instead of falling back to the default preference.
function readLocalStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function readSessionStorage(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

// why: christo 401s a share-link guest token (ticketId or folderId claim, no
// role) on every route outside that ticket or folder, so a tenant-cache prime
// for one can only fail, and the query client retries it with backoff (~7s).
function isShareLinkGuest(user: ShowcaseToken | null): boolean {
  return (
    user != null &&
    user.role == null &&
    (user.ticketId != null || user.folderId != null)
  );
}

export const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "authenticated",
  async loader({
    context,
  }: {
    context: { queryClient: QueryClient; search: any };
    location: { pathname: string };
  }) {
    const { value: user } = store.getState().auth;

    if (!user?.ticketId) {
      const { queryClient } = context;

      // Kick off the folder-tree prefetch but DO NOT await it. The folder
      // tree can be enormous for long-lived workspaces (thousands of
      // nodes on staging admin accounts) and historically blocked every
      // authenticated route for 30s+, hanging Playwright tests on a
      // "Loading…" screen and giving real users a janky cold-start.
      // Consumers (`FolderTreeView` in `AppDrawer`, `TicketGrid`) now
      // read the tree via the `useFolderTree()` hook (stable cache key
      // `["folders", "tree"]`), so they render an empty/skeleton state
      // first and hydrate when the prefetch resolves.
      queryClient.prefetchQuery({
        queryKey: ["folders", "tree"],
        queryFn: getFolderTree,
      });

      // Prime the cache so canAccessAskPuntt() can read the enterprise's
      // askPunttEnabled toggle synchronously in child route guards and the
      // sidebar. Swallow errors: if the fetch fails the gate falls back to
      // false (cache miss) and the rest of the layout must still load.
      if (!isShareLinkGuest(user)) {
        await queryClient
          .ensureQueryData({
            queryKey: ["brand-guidelines"],
            queryFn: getGuidelines,
          })
          .catch(() => undefined);
      }

      return { folderTree: [] };
    }

    return { folderTree: [] };
  },
  async beforeLoad({ search, location }) {
    const { value: user } = store.getState().auth;

    // This is used for redirects after login. It must start with a / or else
    // the path won't match so the redirect will fail.
    const baseLocation = location.pathname;

    if (user == null) {
      throw redirect({
        to: loginRoute.to,
        search: {
          redirect: baseLocation,
        },
      });
    }

    // Prime the enterprise list AND the brand-guidelines toggle BEFORE
    // child route guards run.
    //
    // `isDemoEnterprise()` (used by `routes/index/route.ts`,
    // `routes/vitalora-home/route.tsx`,
    // `routes/vitalora-agents/*Route.tsx`,
    // `routes/vitalora-integrations/route.tsx`, and the sidebar
    // through `isMeaningfulGigsOrDemo()`) reads the `enterprises`
    // cache synchronously. `isAskPunttEnabledForEnterprise()`, used by
    // the default post-auth landing helper in `routes/index/route.ts`,
    // reads the `brand-guidelines` cache synchronously. On a fresh page load
    // (first login) both return `false` until their fetch completes.
    //
    // Awaiting BOTH here in `beforeLoad` (not `loader`) is
    // load-bearing: TanStack Router runs all matched-route
    // `beforeLoad`s top-down before any `loader`, and child routes'
    // `beforeLoad`s read these caches, so a `loader`-level prime is
    // too late. Leaving `brand-guidelines` un-primed here was the
    // post-login routing bug: `indexRoute.beforeLoad` read the toggle as
    // `false` on a cold cache and bounced Ask-Puntt-enabled users to
    // `/tickets` (the Documents page)
    // instead of `/ask-puntt`. Errors are swallowed so a hiccup on
    // `/private/catalyst/enterprises` or `/brand-guidelines` doesn't
    // lock the user out of the layout entirely.
    await Promise.all([
      // why: child beforeLoads read flags sync — undefined before the PostHog callback is a false deny.
      // Swallowed like the sibling primes: `waitForFeatureFlags()` cannot
      // reject today, but a throwing PostHog subscriber would take down the
      // whole authenticated shell instead of degrading to "flags read off",
      // which is the opposite of what this block intends.
      requireFlagsLoaded().catch(() => undefined),
      ...(isShareLinkGuest(user)
        ? []
        : [
            queryClient
              .ensureQueryData({
                queryKey: ["enterprises"],
                queryFn: getEnterprises,
              })
              .catch(() => undefined),
            queryClient
              .ensureQueryData({
                queryKey: ["brand-guidelines"],
                queryFn: getGuidelines,
              })
              .catch(() => undefined),
          ]),
    ]);

    const isTicketRoute = matchPathname(
      BASEPATH,
      location.pathname,
      ticketRoute,
    );
    const isTicketCompatRoute = matchPathname(
      BASEPATH,
      location.pathname,
      ticketCompatibilityRoute,
    );
    const isTicketsRoute = matchPathname(
      BASEPATH,
      location.pathname,
      ticketsRoute,
    );
    const folderTokenPresent =
      "folderId" in search &&
      typeof search.folderId === "string" &&
      readSessionStorage(`${search?.folderId}|token`);

    if (
      user.role == null &&
      ((user.ticketId != null &&
        !isTicketRoute &&
        !isTicketCompatRoute &&
        !folderTokenPresent) ||
        (user.folderId != null &&
          !isTicketsRoute &&
          !isTicketRoute &&
          !isTicketCompatRoute))
    ) {
      store.dispatch(logout());
      throw redirect({
        to: loginRoute.to,
        search: {
          redirect: baseLocation,
        },
      });
    }
    const hasStartedOnboardingFlow =
      !!readLocalStorage("onboarding_flow") ||
      !!readLocalStorage("impersonation_bypass");

    if (shouldRedirectToAddPassword(user, hasStartedOnboardingFlow)) {
      throw redirect({
        to: addPasswordRoute.to,
      });
    }

    if (search.redirect) {
      throw redirect({ to: search.redirect });
    }

    store.dispatch(
      setDrawerOpen({
        // Default the app sidebar to OPEN when no prior preference is in
        // localStorage. The old `=== "true"` check defaulted a fresh
        // session to CLOSED, which (a) hides the Ask Puntt Chats /
        // Projects / "Ask Puntt" sidebar link until the user clicks the
        // toggle and (b) breaks every Playwright session (which starts
        // with empty storage). Matches the `comments` drawer default
        // immediately below and aligns with Slack/Linear/VS Code-style
        // sidebars where the nav is open by default.
        app:
          (readLocalStorage(`app_${SIDEBAR_STORAGE_NAME}`) ?? "true") ===
          "true",
        comments:
          (readLocalStorage(`comments_${SIDEBAR_STORAGE_NAME}`) ?? "true") ===
          "true",
      }),
    );

    return {
      search: search || {},
    };
  },
  component: AuthLayout,
  pendingComponent: PendingComponent,
});

function AuthLayout() {
  useAuthenticationOverlay();
  const router = useRouter();
  const user = useAppSelector((state) => state.auth.value);

  // This is called only when the user logs out
  if (user == null && router.state.location.pathname !== loginRoute.to) {
    // Immediately redirect to prevent any rendering that could cause error messages
    location.assign(logoutUrl(router.basepath));
    // Return null to prevent any rendering while redirecting
    return null;
  }

  // Hide the navigation chrome and other UI elements for the Figma plugin
  // and the Chrome extension.
  if (
    router.state.location.pathname.includes("tickets/figma-plugin") ||
    router.state.location.pathname.includes("tickets/chrome-extension")
  ) {
    return <Outlet />;
  }

  return <PunttLayout />;
}
